import { CheckCircle, Bug, Lightbulb, ListTodo, Clock } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface TaskItem {
  id: string;
  type: string;
  description: string;
  status: string;
  priority?: number;
  created_at?: string;
}

interface TaskListWidgetProps {
  data: { tasks: TaskItem[] };
  onAction?: (actionId: string) => void;
}

const typeIcon: Record<string, typeof ListTodo> = {
  bug:  Bug,
  idea: Lightbulb,
  task: ListTodo,
};

const typeColor: Record<string, string> = {
  bug:  "text-daydra-error",
  idea: "text-daydra-info",
  task: "text-daydra-accent",
};

export function TaskListWidget({ data, onAction: _onAction }: TaskListWidgetProps) {
  const tasks = data?.tasks ?? [];

  if (tasks.length === 0) {
    return (
      <Card variant="default" padding="default" className="max-w-md animate-slide-up">
        <div className="flex items-center gap-3 text-daydra-dim">
          <CheckCircle size={18} className="text-daydra-success" />
          <p className="text-sm">No open tasks</p>
        </div>
      </Card>
    );
  }

  // Group by type
  const bugs = tasks.filter((t) => t.type === "bug");
  const ideas = tasks.filter((t) => t.type === "idea");
  const regularTasks = tasks.filter((t) => t.type === "task" || !["bug", "idea"].includes(t.type));

  return (
    <Card variant="default" padding="none" className="max-w-md animate-slide-up overflow-hidden">
      <CardHeader className="px-4 py-3">
        <div className="flex items-center gap-2 flex-1">
          <ListTodo size={16} className="text-daydra-accent" />
          <CardTitle>Tasks</CardTitle>
          <Badge variant="accent" size="sm">{tasks.length}</Badge>
        </div>
      </CardHeader>

      <ul className="border-t border-daydra-border divide-y divide-daydra-border">
        {[...bugs, ...regularTasks, ...ideas].map((task) => {
          const Icon = typeIcon[task.type] ?? ListTodo;
          const color = typeColor[task.type] ?? "text-daydra-dim";

          return (
            <li
              key={task.id}
              className="flex items-start gap-3 px-4 py-2.5 hover:bg-daydra-raised/50 transition-colors group"
            >
              <Icon size={14} className={cn("shrink-0 mt-0.5", color)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-daydra-text leading-snug">{task.description}</p>
                {task.created_at && (
                  <p className="text-2xs text-daydra-muted mt-0.5">
                    <Clock size={9} className="inline mr-1" />
                    {new Date(task.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              {task.priority && task.priority > 0 && (
                <Badge variant="warning" size="sm">P{task.priority}</Badge>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
